<template>
  <div>
    <q-card
      class="q-py-sm"
      :class="{
        'q-px-auto': $q.screen.xs,
        'column items-center q-px-sm': !$q.screen.xs,
        'q-mx-auto': !checkout && widgetPosition === TEXT_POSITION_TYPES.CENTER,
        'q-mr-auto': widgetPosition === TEXT_POSITION_TYPES.LEFT && !checkout,
        'q-ml-auto': widgetPosition === TEXT_POSITION_TYPES.RIGHT && !checkout,
        'bg-transparent': !checkout && widgetBgColor === 'default',
        [`bg-${widgetBgColor}`]: !checkout && widgetBgColor !== 'custom' && widgetBgColor !== 'default'
      }"
      :style="{
        'background-color': !checkout && widgetBgColor == 'custom' ? widgetBgColorHex : undefined,
        'max-width': widthMode === 'Centered' ? '600px' : $getMaxWidthForScreenSize(isPhonePreview),
      }"
      :square="square"
      :flat="flat"
      data-test-id="donation-widget"
    >
      <q-card-section
        v-if="description || !checkout || $register"
        :class="{ 'q-px-sm q-py-xs': checkout }"
        :style="{
          'max-width': formMaxWidth,
          'width': !isMobileView ? '100%' : undefined,
        }"
      >
        <section-heading
          v-if="!checkout || $register"
          :heading-color="headingColor"
          :heading-color-hex="headingColorHex"
          :heading-font-size="headingFontSize ? headingFontSize : 'h4'"
          :heading-align="headingAlign ? headingAlign : 'left'"
          :heading-type="headingType"
          :heading-background-image-url="headingBackgroundImageUrl"
          :heading-background-image-repeat="headingBackgroundImageRepeat"
          :title="title || defaultTitle"
          :checkout="checkout"
          :is-mobile-view="isMobileView"
        >
          <template #icon>
            <q-icon
              v-if="description && checkout"
              class="col-auto q-mb-xs"
              name="info"
              :size="$q.screen.xs || checkout ? 'xs' : 'md'"
              color="grey"
            >
              <q-tooltip>
                <span style="white-space: pre-line;">{{ description }}</span>
              </q-tooltip>
            </q-icon>
          </template>
        </section-heading>

        <p
          v-if="description && !$register"
          class="text-body2 q-mb-none"
          :class="{
            [`text-${headingAlign}`]: !!headingAlign,
            'text-left': !headingAlign,
            [`text-${getTextColor}`]: !checkout,
            'text-responsive-grey-8': checkout
          }"
        >
          {{ description }}
        </p>
      </q-card-section>
      <q-card-section
        class="column q-my-none"
        :class="{
          'q-px-none': isMobileView || checkout,
          'q-mx-sm': isMobileView,
          'q-py-sm': !description && checkout && !$register,
        }"
        :style="{
          'max-width': formMaxWidth,
          'width': !isMobileView ? '100%' : ''
        }"
      >
        <donation-tip-buttons
          :amount="donationAmount"
          :percentage="donationPercent"
          :type="donationType"
          :percent-options="donationPercentOptions"
          :amount-options="donationAmountOptions"
          :order-total-without-donations-or-tips="order.totalWithoutTipAndDonation"
          :checkout="checkout"
          :is-mobile-view="isMobileView"
          :inline-submit-button="widthMode !== 'Centered' && !checkout && !isMobileView"
          :text-color="getTextColor"
          :button-color="buttonColor"
          :button-color-hex="buttonColorHex"
          :button-border-radius="buttonBorderRadius"
          @update:amount="updateAmount"
          @update:percentage="updatePercentage"
        >
          <template #submit-button>
            <checkout-controls
              v-if="!checkout && !isMobileView && widthMode !== 'Centered'"
              class="transparent col no-margin"
              style="max-width: 33%; padding-top: 0; padding-right: 0; padding-bottom: 0"
              flat
              hide-amounts
              force-large-style
              button-padding="md"
              :button-border-radius="buttonBorderRadius"
              :button-color="buttonColor"
              :button-color-hex="buttonColorHex"
              :button-type="buttonType"
              @next="donate"
            />
          </template>
        </donation-tip-buttons>
      </q-card-section>

      <checkout-controls
        v-if="!checkout && (isMobileView || widthMode === 'Centered')"
        class="transparent"
        :class="{
          'q-py-md': !isMobileView,
          'q-py-sm': isMobileView
        }"
        :button-border-radius="buttonBorderRadius"
        :button-color="buttonColor"
        :button-color-hex="buttonColorHex"
        :button-type="buttonType"
        hide-amounts
        force-large-style
        button-padding="md"
        @next="donate"
      />
    </q-card>
  </div>
</template>

<script>
import OrderItem, { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import { BUTTON_TYPES, TEXT_POSITION_TYPES } from 'src/models/page/Section'
import { DONATION_TIP_TYPES } from 'src/models/site/Site'
import CheckoutControls from 'src/modules/checkout/components/CheckoutControls'
import DonationTipButtons from 'src/modules/checkout/components/DonationTipButtons'
import SectionHeading from 'src/modules/consumer/components/SectionHeading'
export default {
  name: 'DonationWidget',
  components: {
    CheckoutControls,
    DonationTipButtons,
    SectionHeading
  },
  configuration: ['currentCircuit', 'currentSite'],
  provide () {
    return {
      checkoutData: this.checkoutData,
      order: this.order
    }
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    checkoutData: {
      type: Object,
      required: false,
      default: null
    },
    widthMode: {
      type: String,
      required: false,
      default: 'Centered'
    },
    bgColor: {
      type: String,
      default: null,
      required: false
    },
    bgColorHex: {
      type: String,
      default: null,
      required: false
    },
    headingColor: {
      type: String,
      default: 'primary',
      required: false
    },
    headingColorHex: {
      type: String,
      default: '#FFFFFF',
      required: false
    },
    headingAlign: {
      type: String,
      default: null,
      required: false
    },
    headingFontSize: {
      type: String,
      default: null,
      required: false
    },
    headingType: {
      type: String,
      default: 'default',
      required: false
    },
    headingBackgroundImageUrl: {
      type: String,
      default: null,
      required: false
    },
    headingBackgroundImageRepeat: {
      type: Boolean,
      required: false,
      default: true
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    description: {
      type: String,
      required: false,
      default: null
    },
    buttonText: {
      type: String,
      required: false,
      default: null
    },
    buttonType: {
      type: String,
      required: false,
      default: BUTTON_TYPES.STANDARD
    },
    buttonColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    buttonColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    buttonBorderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    donorPerfectCampaignCode: {
      type: String,
      required: false,
      default: null
    },
    checkout: {
      type: Boolean,
      required: false,
      default: false
    },
    square: {
      type: Boolean,
      required: false,
      default: false
    },
    flat: {
      type: Boolean,
      required: false,
      default: false
    },
    demoMode: {
      type: Boolean,
      required: false,
      default: false
    },
    isPhonePreview: {
      type: Boolean,
      required: false,
      default: false
    },
    isMobileView: {
      type: Boolean,
      required: false,
      default: false
    },
    widgetBgColor: {
      type: String,
      required: false,
      default: 'default'
    },
    widgetBgColorHex: {
      type: String,
      required: false,
      default: '#ffffff'
    },
    widgetPosition: {
      type: String,
      required: false,
      default: TEXT_POSITION_TYPES.CENTER
    },
    defaultAmounts: {
      type: Array,
      required: false,
      default () {
        return [1, 2, 5]
      }
    },
    defaultPercents: {
      type: Array,
      required: false,
      default () {
        return [10, 15, 20, 25]
      }
    },
    overrideDonationTypes: {
      type: Array,
      required: false,
      default () {
        return [
          DONATION_TIP_TYPES.AMOUNT,
          DONATION_TIP_TYPES.PERCENT]
      }
    },
    preselectedValue: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      donationId: null,
      donationType: DONATION_TIP_TYPES.AMOUNT,
      donationAmount: 0,
      donationPercent: 0,
      defaultButtonText: 'Donate'
    }
  },
  computed: {
    donationAmountOptions () {
      const options = [...this.defaultAmounts.map(amount => ({
        label: this.$i18n.n(amount, 'currency'),
        value: amount
      }))]

      return _.sortBy(
        this.isMobileView ? options.slice(0, 3) : options,
        option => +option.value
      )
    },
    donationPercentOptions () {
      const options = [...this.defaultPercents.map(percent => ({
        label: `${percent}%`,
        value: percent,
        slot: `${percent}`
      }))]

      return _.sortBy(
        this.isMobileView ? options.slice(0, 3) : options,
        option => +option.value
      )
    },
    defaultAmount () {
      return this.defaultAmounts[0]
    },
    defaultPercent () {
      return this.defaultPercents[0]
    },
    donationTitle () {
      return this.title || (this.checkout && this.currentSite.sendToDonorPerfect && this.currentSite.donorPerfectCampaignTitleForCheckout) || ''
    },
    donationCode () {
      return (this.checkout && this.currentSite.sendToDonorPerfect && this.currentSite.donorPerfectCampaignCodeForCheckout) || this.donorPerfectCampaignCode
    },
    donationDescription () {
      return (this.checkout && this.currentSite.sendToDonorPerfect && this.currentSite.donorPerfectCampaignTitleForCheckout) || this.donorPerfectCampaignCode || this.title || this.$tc('pricing.donation', 1)
    },
    purchasingMembership () {
      return this.checkoutData?.purchasingMembership || this.order?.purchasingMembership
    },
    getTextColor () {
      let colorHex
      if (this.widgetBgColor === 'default' || !this.widgetBgColor) {
        if (this.bgColor === 'custom') {
          colorHex = this.bgColorHex
        } else {
          colorHex = this.$getSiteColorHex(this.currentSite, this.bgColor)
        }
      } else if (this.widgetBgColor === 'custom') {
        colorHex = this.widgetBgColorHex
      } else {
        colorHex = this.$getSiteColorHex(this.currentSite, this.widgetBgColor)
      }
      if (!colorHex) return 'responsive'
      return this.$isColorDark(colorHex) ? 'white' : 'black'
    },
    donationOrderItemFromOrderItemId () {
      return _.find(this.order.orderItems.data, ({ id, isDraft }) => {
        return isDraft && id === this.donationId
      })
    },
    donationOrderItemFromOrderItemDetail () {
      return _.find(
        this.order.orderItems.data,
        orderItem =>
          orderItem.type === ORDER_ITEM_TYPES.DONATION &&
          orderItem.isDraft &&
          ((orderItem.details && !this.donationTitle && orderItem.details[0].title === '') || (orderItem.details && orderItem.details[0].title === this.donationTitle))
      )
    },
    donationOrderItem () {
      return this.donationOrderItemFromOrderItemId || this.donationOrderItemFromOrderItemDetail
    },
    formMaxWidth () {
      if (this.$register) {
        return this.$q.screen.gt.xs ? '440px' : '100%'
      } else if (this.checkout) {
        return this.$q.screen.gt.xs ? '600px' : '100%'
      } else {
        return this.widthMode === 'Centered' ? '100%' : '1100px'
      }
    }
  },
  watch: {
    donationAmount () {
      this.updateDonation()
    },
    'order.loading': {
      handler () {
        this.initializeDonation()
      },
      immediate: true
    },
    donationOrderItem: {
      handler: function () {
        this.donationId = this.donationOrderItem?.id || null
        if (!_.isNil(this.donationId)) {
          this.donationAmount = this.donationOrderItem.amount
          this.donationPercent = this.computePercentFromAmount(this.donationOrderItem.amount)
        }

        if (this.checkoutData) {
          this.checkoutData.canProceed = !_.isNil(this.donationOrderItem) && !this.demoMode
        }
      },
      immediate: true
    }
  },
  created () {
    _.extend(this, { DONATION_TIP_TYPES, BUTTON_TYPES, TEXT_POSITION_TYPES })
    if (this.preselectedValue) {
      if (this.donationType === DONATION_TIP_TYPES.AMOUNT) {
        this.donationAmount = this.defaultAmount
        this.donationPercent = this.computePercentFromAmount(this.defaultAmount)
      } else {
        this.donationPercent = this.defaultPercent
        this.donationAmount = this.computeAmountFromPercent(this.defaultPercent)
      }
    }
  },
  unmounted () {
    if (this.donationOrderItem?.amount === 0) {
      this.removeDonationOrderItem(this.donationOrderItem)
    }
  },
  methods: {
    createDonationOrderItem () {
      const orderItem = new OrderItem({
        type: ORDER_ITEM_TYPES.DONATION,
        description: this.donationDescription,
        details: [{
          title: this.donationTitle,
          donorPerfectCampaignCode: this.donationCode
        }]
      })

      this.donationId = orderItem.id

      return orderItem
    },
    removeDonationOrderItem (donation) {
      this.order.removeOrderItem(donation)
      this.donationId = null
    },
    updateDonation () {
      const value = this.donationAmount

      if (value <= 0 && !_.isNil(this.donationOrderItem)) {
        this.removeDonationOrderItem(this.donationOrderItem)
        this.resetDonationType()
        return
      } else if (value > 0 && _.isNil(this.donationOrderItem)) {
        this.order.orderItems.add(this.createDonationOrderItem())
      }

      if (value > 0) {
        this.donationOrderItem.amount = value
      }
    },
    resetDonationType () {
      if (this.overrideDonationTypes.length > 1) {
        this.donationType = this.order.totalWithoutTipAndDonation > 10 && (
          !this.donationOrderItem || this.donationOrderItem.amount === 0 || _.includes(this.defaultPercents, this.computePercentFromAmount(this.donationOrderItem.amount))
        )
          ? DONATION_TIP_TYPES.PERCENT
          : DONATION_TIP_TYPES.AMOUNT
      } else {
        this.donationType = _.first(this.overrideDonationTypes)
      }
    },
    async initializeDonation () {
      if (this.donationOrderItem?.amount === 0) {
        this.removeDonationOrderItem(this.donationOrderItem)
      }

      if (!this.demoMode && this.order.isRemote) {
        await this.order.ensureLoaded()
      }
      this.resetDonationType()

      if (this.donationOrderItem) {
        const amount = this.donationOrderItem.amount
        this.donationAmount = amount
        this.donationPercent = this.computePercentFromAmount(amount)
      }

      if (!this.checkoutData) return

      this.checkoutData.showInfo = false
      this.checkoutData.nextButtonLabel = this.buttonText || this.defaultButtonText
      this.checkoutData.showEmptyCartNextLabel = false
      this.checkoutData.canProceed = !this.demoMode
    },
    computeAmountFromPercent (percent) {
      return Math.round(this.order.totalWithoutTipAndDonation * percent) / 100.0
    },
    computePercentFromAmount (amount) {
      if (this.order.totalWithoutTipAndDonation <= 0) return 0

      return Math.round((amount / this.order.totalWithoutTipAndDonation) * 100.0)
    },
    donate () {
      if (this.demoMode) return

      const isDigitalOnly = (this.order?.purchasingGiftCards || this.order?.purchasingVouchers || this.order?.purchasingDonation || this.purchasingMembership) && !(this.order?.purchasingTickets || this.order?.purchasingItems)
      if (isDigitalOnly && this.$q.screen.xs) {
        this.$router.push('/checkout/payment')
      } else {
        this.$router.push('/checkout/cart')
      }
    },
    updateAmount (value) {
      this.donationAmount = value
    },
    updatePercentage (value) {
      this.donationPercent = value
    }
  }

}

</script>

<style lang="scss" scoped>
h2 {
  color: var(--q-primary);
  font-weight: bold;
  line-height: 1em;
}
</style>
